// Eskom PARCC — resolve CSS theme tokens to concrete colour strings for charts.
//
// Recharts needs real colour values (it cannot consume `var(--x)` for fills/
// strokes), so this hook reads the computed CSS custom properties off <html> and
// returns a typed palette. It re-resolves whenever the theme changes, so charts
// recolour when the user toggles dark/light.
import { useEffect, useState } from 'react';
import { useTheme } from './theme';
import { useVariant } from './uiPrefs';

export interface ThemeColors {
  text: string;
  muted: string;
  faint: string;
  line: string;
  panel: string;
  surfaceRaised: string;
  accent: string;
  accent2: string;
  ok: string;
  warn: string;
  bad: string;
  house: string;
  na: string;
}

function readVar(styles: CSSStyleDeclaration, name: string, fallback: string): string {
  const v = styles.getPropertyValue(name).trim();
  return v || fallback;
}

function resolve(): ThemeColors {
  // jsdom / SSR guard: getComputedStyle may be unavailable or empty.
  const styles =
    typeof window !== 'undefined' && typeof getComputedStyle === 'function'
      ? getComputedStyle(document.documentElement)
      : ({ getPropertyValue: () => '' } as unknown as CSSStyleDeclaration);
  return {
    text: readVar(styles, '--text', '#f3f8fc'),
    muted: readVar(styles, '--muted', '#abc3d8'),
    faint: readVar(styles, '--faint', '#6f8aa0'),
    line: readVar(styles, '--line', '#1f5d87'),
    panel: readVar(styles, '--panel', '#0b2135'),
    surfaceRaised: readVar(styles, '--surface-raised', 'rgba(255,255,255,0.045)'),
    accent: readVar(styles, '--accent', '#00a3e0'),
    accent2: readVar(styles, '--accent-2', '#0072ce'),
    ok: readVar(styles, '--ok', '#3bb273'),
    warn: readVar(styles, '--warn', '#f2a900'),
    bad: readVar(styles, '--bad', '#d64545'),
    house: readVar(styles, '--house', '#69c5e4'),
    na: readVar(styles, '--na', '#7f8faa'),
  };
}

/** The current theme's chart palette, re-resolved whenever either UI axis moves:
 *  the light/dark sub-mode (`data-theme`) OR the Legacy/Lumen variant
 *  (`data-ui`), which swaps the whole token set. */
export function useThemeColors(): ThemeColors {
  const { theme } = useTheme();
  const variant = useVariant();
  const [colors, setColors] = useState<ThemeColors>(() => resolve());
  useEffect(() => {
    // Re-read once the attribute + CSS have applied. The attribute is stamped
    // synchronously by the writers, but a second read on the next frame also
    // covers a stylesheet that has only just become active.
    setColors(resolve());
    if (typeof requestAnimationFrame !== 'function') return;
    const frame = requestAnimationFrame(() => setColors(resolve()));
    return () => cancelAnimationFrame(frame);
  }, [theme, variant]);
  return colors;
}
